import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LogOut, Menu, X, FileText, ShieldCheck, Users, BarChart3, Tag, Calendar, Bell, MapPin, Settings as SettingsIcon, Zap, HelpCircle, Home as HomeIcon, Gift } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { PrimeVisionLogo, PrimeVisionMark } from "@/components/PrimeVisionLogo";
import ResponsabilitaGate from "@/components/ResponsabilitaGate";
import MedicoTour, { MEDICO_TOUR_STEPS } from "@/components/MedicoTour";
import AssistantPanel from "@/components/AssistantPanel";

const NAV_BY_ROLE = {
  admin: [
    { to: "/admin", label: "Admin Primevision", icon: ShieldCheck },
  ],
  medico: [
    { to: "/home", label: "Home", icon: HomeIcon },
    { to: "/visite", label: "Visite", icon: FileText },
    { to: "/agenda", label: "Agenda", icon: Calendar },
    { to: "/pazienti", label: "Pazienti", icon: Users },
    { to: "/sedi", label: "Sedi", icon: MapPin },
    { to: "/automazioni", label: "Automazioni", icon: Zap },
    { to: "/impostazioni", label: "Impostazioni", icon: SettingsIcon },
    { to: "/extra", label: "Extra", icon: Gift },
  ],
};

export default function AppShell({ children, title, subtitle, rightSlot }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  // Gate accesso responsabile medico: forzato finché non accetta la versione corrente
  const [respAccepted, setRespAccepted] = useState(false);
  const showResponsabilita = user && (user.role === "medico" || user.role === "ortottista") && !respAccepted;

  // Phase 31: Tour guidato medico (auto-start al primo login se non completato)
  const [tourOpen, setTourOpen] = useState(false);
  const [tourReady, setTourReady] = useState(false);
  // Fase I: prompt guida separato dal gate responsabilità (mai sovrapposti)
  const [tourPromptOpen, setTourPromptOpen] = useState(false);
  const [tourNeverAsk, setTourNeverAsk] = useState(false);

  const openTour = useCallback(async () => {
    // Reset lato server per garantire re-play consistente
    try { await api.post("/medico/tour", { action: "reset" }); } catch { /* noop */ }
    // Reset step client-side così parte da 0 e riappare la welcome
    try {
      window.localStorage.removeItem(`pv-tour-step-${user?.id || "anonymous"}`);
      window.localStorage.removeItem(`pv-tour-welcome-${user?.id || "anonymous"}`);
    } catch { /* noop */ }
    setTourOpen(true);
  }, [user]);

  const closeTour = useCallback(async (action = "skip") => {
    setTourOpen(false);
    try { await api.post("/medico/tour", { action }); } catch { /* noop */ }
    // Al termine (complete/skip) svuota lo step corrente per non rimanere a metà
    try {
      window.localStorage.removeItem(`pv-tour-step-${user?.id || "anonymous"}`);
    } catch { /* noop */ }
  }, [user]);

  useEffect(() => {
    // Il prompt guida parte SOLO dopo l'accettazione del consenso responsabilità (mai sovrapposto)
    if (!user || user.role !== "medico" || tourReady || showResponsabilita) return;
    (async () => {
      try {
        const { data } = await api.get("/medico/tour");
        setTourReady(true);
        if (!data?.completed && !data?.skipped) {
          const declined = window.sessionStorage.getItem(`pv-tour-declined-${user.id}`) === "1";
          if (!declined) setTimeout(() => setTourPromptOpen(true), 800);
        }
      } catch {
        setTourReady(true);
      }
    })();
  }, [user, tourReady, showResponsabilita]);

  useEffect(() => {
    if (!user || user.role !== "medico") return;
    const load = async () => {
      try { const { data } = await api.get("/notifications"); setNotifs(data); } catch { /* noop */ }
    };
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [user]);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setNotifs((p) => ({ ...p, unread: 0, items: p.items.map((x) => ({ ...x, read: true })) })); } catch { /* noop */ }
  };

  if (!user) return null;
  const items = NAV_BY_ROLE[user.role] || [];
  const roleLabel = user.role === "medico" ? "Oculista" : (user.role === "admin" ? "Admin Primevision" : user.role);
  const displayName = user.role === "medico" ? `Oculista ${(user.name || "").replace(/^Dott\.?ssa?\s+/i, "").trim()}` : user.name;

  const Sidebar = (
    <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-[#E2E8F0] shrink-0">
      <div className="px-6 py-6">
        <PrimeVisionLogo size={36} />
      </div>
      <nav className="flex-1 px-3 py-2 space-y-1" data-testid="sidebar-nav">
        {items.map((it) => {
          const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold transition-colors ${
                active ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100 hover:text-brand-navy"
              }`}
            >
              <Icon size={18} />
              <span>{it.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[#E2E8F0]">
        <div className="px-2 py-2 text-xs text-slate-500">
          <div className="font-bold text-brand-navy">{displayName}</div>
          <div>{roleLabel}</div>
        </div>
        {user.role === "medico" && (
          <button
            data-testid="tour-replay-btn"
            onClick={openTour}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-brand-azure hover:bg-sky-50 hover:text-brand-navy transition-colors"
            title="Rivedi la guida di benvenuto"
          >
            <HelpCircle size={16} /> Rivedi guida
          </button>
        )}
        <button
          data-testid="logout-button"
          onClick={() => { logout(); nav("/login"); }}
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut size={16} /> Esci
        </button>
      </div>
    </aside>
  );

  return (
    <div className="App min-h-screen flex bg-brand-bg">
      {Sidebar}
      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} data-testid="mobile-menu-backdrop">
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 flex items-center justify-between border-b">
              <PrimeVisionLogo size={32} />
              <button onClick={() => setOpen(false)} data-testid="close-mobile-menu"><X /></button>
            </div>
            <nav className="p-3 space-y-1" data-testid="mobile-nav">
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mnav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    <Icon size={18} /> {it.label}
                  </Link>
                );
              })}
              {user.role === "medico" && (
                <button
                  data-testid="tour-replay-mobile-btn"
                  onClick={() => { setOpen(false); openTour(); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-brand-azure hover:bg-sky-50"
                >
                  <HelpCircle size={16} /> Rivedi guida
                </button>
              )}
              <button onClick={() => { logout(); nav("/login"); }} className="w-full flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50">
                <LogOut size={16} /> Esci
              </button>
            </nav>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(true)} data-testid="open-menu-button">
                <Menu />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-brand-navy truncate" data-testid="page-title">{title}</h1>
                {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {rightSlot}
              {user.role === "medico" && (
                <div className="relative">
                  <button data-testid="notif-bell" onClick={() => setNotifOpen(!notifOpen)} className="relative p-2 rounded-full hover:bg-slate-100">
                    <Bell size={18} className="text-brand-navy" />
                    {notifs.unread > 0 && <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-brand-orange text-white text-[10px] font-extrabold flex items-center justify-center">{notifs.unread}</span>}
                  </button>
                  {notifOpen && (
                    <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-white border border-[#E2E8F0] rounded-xl shadow-xl z-50" data-testid="notif-panel">
                      <div className="flex items-center justify-between p-3 border-b border-[#E2E8F0]">
                        <div className="font-extrabold text-brand-navy text-sm">Notifiche</div>
                        {notifs.unread > 0 && <button onClick={markAll} className="text-xs text-brand-azure font-bold">Segna tutte</button>}
                      </div>
                      {notifs.items.length === 0 ? (
                        <div className="p-4 text-sm text-slate-400 text-center">Nessuna notifica</div>
                      ) : (
                        <ul>
                          {notifs.items.slice(0, 20).map((n) => (
                            <li key={n.id} data-testid={`notif-${n.id}`} className={`p-3 border-b border-[#E2E8F0] ${n.read ? "" : "bg-brand-azure/5"}`}>
                              <div className="font-bold text-brand-navy text-sm">{n.title}</div>
                              <div className="text-xs text-slate-500">{n.message}</div>
                              <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-bg border border-[#E2E8F0]">
                <span className="w-2 h-2 rounded-full bg-brand-azure animate-pulse"></span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-600" data-testid="topbar-role-label">{roleLabel}</span>
              </div>
            </div>
          </div>
        </header>
        <div className="px-4 sm:px-6 lg:px-8 py-6 pb-28" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 7rem)" }}>{children}</div>
      </main>
      {/* Assistente IA interno: sempre raggiungibile per il medico dopo login */}
      {user?.role === "medico" && !showResponsabilita && <AssistantPanel />}
      {showResponsabilita && (
        <ResponsabilitaGate user={user} onAccepted={() => setRespAccepted(true)} />
      )}
      {/* Fase 31/38: Tour guidato medico (action-based) */}
      {user?.role === "medico" && tourPromptOpen && !showResponsabilita && (
        <div className="fixed inset-0 z-[95] bg-slate-900/60 flex items-center justify-center p-4" data-testid="tour-prompt-modal" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="font-extrabold text-brand-navy text-lg">Vuoi vedere la guida?</h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Una guida interattiva ti mostra come funziona il software: cartella clinica, anamnesi a toggle, refrazione, copia OD/OS, dettatura IA, salute occhi, chirurgia e consensi. Potrai riaprirla in qualsiasi momento dal pulsante Guida.
            </p>
            <label className="mt-4 flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="tour-prompt-never-label">
              <input type="checkbox" checked={tourNeverAsk} onChange={(e) => setTourNeverAsk(e.target.checked)} className="rounded border-slate-400" data-testid="tour-prompt-never" />
              Non chiedermelo più
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button
                data-testid="tour-prompt-no"
                onClick={async () => {
                  setTourPromptOpen(false);
                  if (tourNeverAsk) { try { await api.post("/medico/tour", { action: "skip" }); } catch { /* noop */ } }
                  else { try { window.sessionStorage.setItem(`pv-tour-declined-${user.id}`, "1"); } catch { /* noop */ } }
                }}
                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100"
              >
                No, non ora
              </button>
              <button
                data-testid="tour-prompt-yes"
                onClick={() => { setTourPromptOpen(false); setTourOpen(true); }}
                className="px-5 py-2 rounded-lg text-sm font-bold bg-brand-navy hover:bg-brand-navyDark text-white"
              >
                Sì, mostrami la guida
              </button>
            </div>
          </div>
        </div>
      )}
      {user?.role === "medico" && (
        <MedicoTour
          open={tourOpen}
          steps={MEDICO_TOUR_STEPS}
          userId={user?.id}
          onOpenMobileMenu={() => setOpen(true)}
          onFinish={() => closeTour("complete")}
          onSkip={() => closeTour("skip")}
          onClose={() => closeTour("skip")}
        />
      )}
    </div>
  );
}
